import tw from 'twin.macro'
import React, { useState } from 'react'
import { isEmpty } from 'lodash'
import { CgAdd, CgChevronRightO } from 'react-icons/cg'
import * as Popover from '@radix-ui/react-popover'

import { allUnits, distanceUnits, legacyDistances } from 'common/constants/unitConstants'
import { buttonBase, buttonVariants, buttonSizes, Button } from 'common/components/Button/Button'
import Tag from 'common/components/Tag/Tag'
import { TextHint } from 'common/components/TextHint/TextHint'

const availableUnits = [
  { value: allUnits['lb/kg'], displayValue: 'Weight (pounds/kilograms)' },
  { value: 'distance', displayValue: 'Distance' },
  { value: allUnits.reps, displayValue: 'Reps' },
  { value: allUnits.time, displayValue: 'Time' },
]

const availableDistances = [
  { value: distanceUnits.in, displayValue: 'inches' },
  { value: distanceUnits.cm, displayValue: 'centimeters' },
  { value: distanceUnits.ft, displayValue: 'feet' },
  { value: distanceUnits.m, displayValue: 'meters' },
  { value: distanceUnits.yd, displayValue: 'yard' },
  { value: distanceUnits.mi, displayValue: 'miles' },
  { value: distanceUnits.km, displayValue: 'kilometers' },
]
const availableDistanceValues = availableDistances.map((distance) => distance.value)

export const getAdjustedUnit = (unit) => {
  const isLegacyDistanceUnit = legacyDistances[unit]
  if (isLegacyDistanceUnit) {
    return unit.split('/')?.[1] || unit // we changed distance units to be a full set of imperial and metric units, accounting for already existing "imperial/metric" units by defaulting to metric unit
  } else {
    return unit
  }
}

export default function AddUnitButton({
  units = [],
  interpFrom,
  handleSave,
  customTrigger,
  onOpenChange,
  isPortalled = true,
  showHint = false,
}) {
  const [selectedUnits, setSelectedUnits] = useState(units)
  const [unitsInterpFrom, setUnitsInterpFrom] = useState(interpFrom)
  const showSave = !isEmpty(units) || !isEmpty(selectedUnits)
  const [selectedUnitType, setSelectedUnitType] = useState(null)

  const unusedUnits = availableUnits.filter((unit) => {
    if (unit.value === 'distance') {
      //since distance has multiple values, we need to check if any of the distance values are already in the units array
      return !selectedUnits.find((unitValue) => availableDistanceValues.includes(getAdjustedUnit(unitValue)))
    } else {
      return !selectedUnits.find((unitValue) => unitValue === unit.value)
    }
  })

  return (
    <div className='relative'>
      <Popover.Root
        onOpenChange={(open) => {
          setSelectedUnitType(null)
          setSelectedUnits(units)
          setUnitsInterpFrom(interpFrom)
          if (onOpenChange) {
            onOpenChange(open)
          }
        }}
      >
        {customTrigger ? (
          customTrigger
        ) : (
          <Popover.Trigger css={[buttonBase, buttonVariants.secondary, buttonSizes.sm]}>
            Add <CgAdd className='w-4 h-4 ml-2' />
          </Popover.Trigger>
        )}
        {isPortalled ? (
          <Popover.Portal>
            <Content
              selectedUnits={selectedUnits}
              setSelectedUnits={setSelectedUnits}
              selectedUnitType={selectedUnitType}
              setSelectedUnitType={setSelectedUnitType}
              unitsInterpFrom={unitsInterpFrom}
              setUnitsInterpFrom={setUnitsInterpFrom}
              handleSave={showSave ? handleSave : null}
              unusedUnits={unusedUnits}
              showHint={showHint}
            />
          </Popover.Portal>
        ) : (
          <Content
            selectedUnits={selectedUnits}
            setSelectedUnits={setSelectedUnits}
            selectedUnitType={selectedUnitType}
            setSelectedUnitType={setSelectedUnitType}
            unitsInterpFrom={unitsInterpFrom}
            setUnitsInterpFrom={setUnitsInterpFrom}
            handleSave={showSave ? handleSave : null}
            unusedUnits={unusedUnits}
            showHint={showHint}
          />
        )}
      </Popover.Root>
    </div>
  )
}

function Content({
  selectedUnits,
  setSelectedUnits,
  selectedUnitType,
  setSelectedUnitType,
  unitsInterpFrom,
  setUnitsInterpFrom,
  handleSave,
  unusedUnits,
  showHint,
}) {
  return (
    <Popover.Content
      className='bg-white px-3 py-3 rounded-xl w-72 shadow-2xl z-30 border-2 border-gray-300'
      aria-label='add-card-content'
    >
      <Popover.Arrow offset={12} className='fill-gray-300' />
      <NavigationActions
        handleBack={() => setSelectedUnitType(null)}
        handleSave={handleSave ? () => handleSave(selectedUnits) : null}
        canGoBack={selectedUnitType === 'distance'}
      />
      {selectedUnits?.length > 0 && (
        <div className='mb-4 mt-3'>
          <div className='text-xs font-bold mb-1'>Selected units:</div>
          {selectedUnits.map((unit) => (
            <Tag
              key={getAdjustedUnit(unit)}
              text={getAdjustedUnit(unit)}
              tooltipText={Boolean(unitsInterpFrom?.[unit]) ? unitsInterpFrom[unit] : null}
              remove={() => {
                setSelectedUnits(selectedUnits.filter((u) => u !== unit))
                if (unitsInterpFrom?.[unit]) {
                  setUnitsInterpFrom({ ...unitsInterpFrom, [unit]: null })
                }
              }}
              css={tw`mb-0 bg-black text-white rounded-md text-xs`}
            />
          ))}
        </div>
      )}
      {selectedUnits.length >= 2 ? (
        <p className='text-sm'>You can select up to 2 units, delete one to make room.</p>
      ) : selectedUnitType !== 'distance' ? (
        <UnitOptions
          options={unusedUnits}
          onSelect={(value) => {
            if (value !== 'distance') {
              setSelectedUnits([...selectedUnits, value])
              if (unitsInterpFrom?.[value]) {
                setUnitsInterpFrom({ ...unitsInterpFrom, [value]: null })
              }
            } else {
              setSelectedUnitType('distance')
            }
          }}
        />
      ) : (
        <UnitOptions
          options={availableDistances}
          onSelect={(value) => {
            if (value !== 'distance') {
              setSelectedUnitType(null)
              setSelectedUnits([...selectedUnits, value])
              if (unitsInterpFrom?.[value]) {
                setUnitsInterpFrom({ ...unitsInterpFrom, [value]: null })
              }
            }
          }}
        />
      )}
      {showHint && (
        <div className='mt-2'>
          <TextHint
            hintText='How are units auto-populated?'
            expandedText='Units are automatically populated from the Exercise Library, as well as the current exercise’s instructions, timer, and stopwatch.'
          />
        </div>
      )}
    </Popover.Content>
  )
}

function UnitOptions({ options, onSelect }) {
  return (
    <div className='flex flex-col'>
      {options.map(({ value, displayValue }) => (
        <Button
          key={value}
          onClick={() => onSelect(value)}
          variant='secondary'
          size='md'
          css={tw`flex-1 flex px-4 mb-2 last:mb-0`}
        >
          <span className='first-letter:capitalize text-left flex-1'>{displayValue}</span>
          {value === 'distance' && <CgChevronRightO className='w-[18px] h-[18px]' />}
        </Button>
      ))}
    </div>
  )
}

const baseButtonClasses = tw`text-tGreen rounded-lg px-2 py-1 hover:bg-tGreen hover:bg-opacity-10 transition-colors`

function NavigationActions({ handleBack, handleSave, canGoBack }) {
  if (canGoBack) {
    return (
      <div className='flex justify-between items-center mb-2'>
        <button onClick={handleBack} css={baseButtonClasses}>
          Back
        </button>
        <div className='flex flex-col items-center mx-2'>
          <h4 className='font-bold text-tBlack first-letter:uppercase'>Distance</h4>
        </div>
        <div className='w-12' />
      </div>
    )
  }

  return (
    <div className='flex justify-between items-center mb-2'>
      <Popover.Close css={baseButtonClasses}>Cancel</Popover.Close>
      {Boolean(handleSave) && (
        <Popover.Close css={[baseButtonClasses, tw`font-bold`]} onClick={handleSave}>
          Save
        </Popover.Close>
      )}
    </div>
  )
}
